import axiosInstance from './axiosInstance';

// 视频审核相关API
export const videoAuditApi = {
  /**
   * 获取网课审核任务列表
   * @param {number} page 页码
   * @param {number} size 每页数量
   * @returns {Promise} 返回任务列表响应
   */
  getVideoAuditTasks: async (page = 1, size = 10) => {
    try {
      const response = await axiosInstance.get(`video-audit/tasks`, {
        params: {
          page,
          page_size: size
        }
      });
      return response;
    } catch (error) {
      throw new Error(error.response?.data?.message || '获取任务列表失败');
    }
  },

  /**
   * 上传视频文件创建任务
   * @param {string} taskName 任务名称
   * @param {File} file 视频文件
   * @param {Function} onProgress 上传进度回调函数
   * @returns {Promise} 返回创建任务响应
   */
  uploadVideo: async (taskName, file, onProgress) => {
    try {
      const formData = new FormData();
      formData.append('task_name', taskName);
      formData.append('file', file);
      
      const response = await axiosInstance.post(`video-audit/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 300000, // 300秒(5分钟)超时
        onUploadProgress: onProgress
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '上传视频失败');
    }
  },

  /**
   * 获取任务详情
   * @param {number} taskId 任务ID
   * @returns {Promise} 返回任务详情响应
   */
  getVideoAuditTaskDetail: async (taskId) => {
    try {
      const response = await axiosInstance.get(`video-audit/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '获取任务详情失败');
    }
  },

  /**
   * 获取任务结果
   * @param {number} taskId 任务ID
   * @returns {Promise} 返回任务结果响应
   */
  getVideoAuditTaskResult: async (taskId) => {
    try {
      const response = await axiosInstance.get(`video-audit/tasks/${taskId}/result`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '获取任务结果失败');
    }
  },

  /**
   * 开始审核任务
   * @param {number} taskId 任务ID
   * @param {Object} options 审核选项
   * @returns {Promise} 返回开始审核响应
   */
  startAudit: async (taskId, options) => {
    try {
      const response = await axiosInstance.post(`video-audit/tasks/${taskId}/start`, options);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '开始审核失败');
    }
  },

  /**
   * 重试审核任务
   * @param {number} taskId 任务ID
   * @returns {Promise} 返回重试审核响应
   */
  retryAudit: async (taskId) => {
    try {
      const response = await axiosInstance.post(`video-audit/tasks/${taskId}/retry`, {});
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '重试审核失败');
    }
  },

  /**
   * 删除审核任务
   * @param {number} taskId 任务ID
   * @returns {Promise} 返回删除任务响应
   */
  deleteVideoAuditTask: async (taskId) => {
    try {
      const response = await axiosInstance.delete(`video-audit/tasks/${taskId}`);
      return response;
    } catch (error) {
      throw new Error(error.response?.data?.message || '删除任务失败');
    }
  },

  /**
   * 手动检查任务状态
   * @param {number} taskId 任务ID
   * @returns {Promise} 返回检查状态响应
   */
  checkTaskStatus: async (taskId) => {
    try {
      const response = await axiosInstance.get(`video-audit/tasks/${taskId}/status`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '检查任务状态失败');
    }
  },

  /**
   * 获取AI API配置
   * @returns {Promise} 返回AI API配置响应
   */
  getAiApiConfig: async () => {
    try {
      const response = await axiosInstance.get(`video-audit/ai-api-config`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '获取AI API配置失败');
    }
  },

  /**
   * 更新任务时长信息
   * @param {number} taskId 任务ID
   * @param {number} duration 时长(毫秒)
   * @returns {Promise} 返回更新时长响应
   */
  updateTaskDuration: async (taskId, duration) => {
    try {
      const response = await axiosInstance.post(`video-audit/tasks/${taskId}/update-duration`, {
        duration: duration
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '更新任务时长失败');
    }
  },

  /**
   * 更新用户网课审核时长
   * @param {number} duration 时长(毫秒)
   * @returns {Promise} 返回更新网课审核时长响应
   */
  updateUserVideoDuration: async (duration) => {
    try {
      const response = await axiosInstance.post(`users/update-video-duration`, {
        duration: duration
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '更新用户网课审核时长失败');
    }
  },

  /**
   * 更新用户收费接口使用时长
   * @param {number} duration 时长(秒)
   * @returns {Promise} 返回更新收费接口使用时长响应
   */
  updateUserPlasoApiDuration: async (duration) => {
    try {
      const response = await axiosInstance.post(`users/update-plaso-api-duration`, {
        duration: duration
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '更新用户收费接口使用时长失败');
    }
  },

  /**
   * 刷新视频/音频链接
   * @param {number} taskId 任务ID
   * @returns {Promise} 返回刷新后的链接响应
   */
  refreshMediaUrl: async (taskId) => {
    try {
      const response = await axiosInstance.get(`video-audit/tasks/${taskId}/refresh-media-url`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '刷新媒体链接失败');
    }
  },

  /**
   * 更新任务审核状态
   * @param {number} taskId 任务ID
   * @param {boolean} isReviewed 是否已审核
   * @returns {Promise} 返回更新审核状态响应
   */
  updateTaskAuditStatus: async (taskId, isReviewed) => {
    try {
      const response = await axiosInstance.patch(`video-audit/tasks/${taskId}/audit-status`, {
        is_reviewed: isReviewed
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '更新任务审核状态失败');
    }
  },

  /**
   * 创建已存档课程的审核任务
   * @param {Object} data 任务数据
   * @returns {Promise} 返回创建任务响应
   */
  createAuditTaskFromDownload: async (data) => {
    const token = localStorage.getItem('token');
    return axiosInstance.post(`video-audit/create-from-download`, data, {
      headers: {
        'Authorization': token
      }
    });
  }
};

// 单独存档方法
export const getVideoAuditTasks = videoAuditApi.getVideoAuditTasks;
export const uploadVideo = videoAuditApi.uploadVideo;
export const getVideoAuditTaskDetail = videoAuditApi.getVideoAuditTaskDetail;
export const getVideoAuditTaskResult = videoAuditApi.getVideoAuditTaskResult;
export const startAudit = videoAuditApi.startAudit;
export const retryAudit = videoAuditApi.retryAudit;
export const deleteVideoAuditTask = videoAuditApi.deleteVideoAuditTask;
export const checkTaskStatus = videoAuditApi.checkTaskStatus;
export const getAiApiConfig = videoAuditApi.getAiApiConfig;
export const updateTaskDuration = videoAuditApi.updateTaskDuration;
export const updateTaskAuditStatus = videoAuditApi.updateTaskAuditStatus;
export const refreshMediaUrl = videoAuditApi.refreshMediaUrl;
export const createAuditTaskFromDownload = videoAuditApi.createAuditTaskFromDownload;